import { FUND_CATEGORIES } from '../budgetPlanning.js';
import { dateStringParts } from '../dateString.js';
import { lineKeyId, } from './projection.js';
export function operatingIncome(income) {
    return income.filter((group) => !FUND_CATEGORIES.has(group.label));
}
export const ZERO_AMOUNT = {
    actual: 0,
    scheduled: 0,
    inflation: 0,
    adjustment: 0,
    accrued: 0,
};
const ZERO = ZERO_AMOUNT;
export function yearTotal(amount) {
    return amount.actual + amount.scheduled + amount.inflation + amount.adjustment + amount.accrued;
}
export function addYearAmounts(a, b) {
    return {
        actual: a.actual + b.actual,
        scheduled: a.scheduled + b.scheduled,
        inflation: a.inflation + b.inflation,
        adjustment: a.adjustment + b.adjustment,
        accrued: a.accrued + b.accrued,
    };
}
export function sumColumns(rows, width) {
    return Array.from({ length: width }, (_, i) => rows.reduce((sum, row) => addYearAmounts(sum, row.years[i] ?? ZERO), ZERO));
}
export function sumMonths(rows, width) {
    return Array.from({ length: width }, (_, offset) => {
        const series = rows.map((row) => row.months[offset]);
        if (series.length === 0 || series.some((months) => months === undefined))
            return undefined;
        return Array.from({ length: 12 }, (_, m) => series.reduce((sum, months) => addYearAmounts(sum, months?.[m] ?? ZERO), ZERO));
    });
}
function cellParts(cell) {
    switch (cell.kind) {
        case 'actual':
            return { actual: cell.amount, scheduled: 0, accrued: 0 };
        case 'scheduled':
            return { actual: 0, scheduled: cell.amount, accrued: 0 };
        case 'accrued':
            return { actual: 0, scheduled: 0, accrued: cell.amount };
        default: {
            const _exhaustive = cell;
            throw new Error(`Unhandled cell kind: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
function lineIdentity(key) {
    switch (key.kind) {
        case 'taxonomy':
            return {
                label: key.subcategory,
                groupId: `${key.type}:${key.category}`,
                groupLabel: key.category,
                status: 'mapped',
            };
        case 'unmapped':
            return {
                label: key.categoryText,
                groupId: `${key.type}:unmapped`,
                groupLabel: 'Not mapped to the taxonomy',
                status: 'unmapped',
            };
        case 'uncategorized':
            return {
                label: key.type === 'income' ? 'Income with no category' : 'Expense with no category',
                groupId: `${key.type}:uncategorized`,
                groupLabel: 'No category',
                status: 'uncategorized',
            };
        default: {
            const _exhaustive = key;
            throw new Error(`Unhandled line key: ${JSON.stringify(_exhaustive)}`);
        }
    }
}
export function buildProjectionModel(pnl, options) {
    const currentFiscalYear = pnl.periods[0]?.fiscalYear ?? Number(pnl.asOfDate.slice(0, 4));
    const lastFiscalYear = pnl.periods.at(-1)?.fiscalYear ?? currentFiscalYear;
    const fiscalYears = Array.from({ length: lastFiscalYear - currentFiscalYear + 1 }, (_, i) => currentFiscalYear + i);
    const monthsOf = fiscalYears.map((fiscalYear) => {
        const months = pnl.periods.flatMap((period) => period.fiscalYear === fiscalYear ? [{ year: period.year, month: period.month }] : []);
        return months.length === 12 ? months : undefined;
    });
    const asOf = dateStringParts(pnl.asOfDate);
    const asOfMonth = monthsOf.reduce((found, calendar, offset) => {
        if (found !== undefined || calendar === undefined)
            return found;
        const monthIndex = calendar.findIndex((m) => m.year === asOf.year && m.month === asOf.month);
        return monthIndex === -1 ? undefined : { offset, monthIndex };
    }, undefined);
    const groups = new Map();
    for (const line of pnl.lines) {
        const identity = lineIdentity(line.key);
        const years = fiscalYears.map(() => ({ ...ZERO }));
        const months = monthsOf.map((calendar) => calendar === undefined ? undefined : calendar.map(() => ({ ...ZERO })));
        const inflationBase = fiscalYears.map(() => 0);
        const fixedScheduled = fiscalYears.map(() => 0);
        const fixedScheduledMonths = monthsOf.map((calendar) => calendar === undefined ? undefined : calendar.map(() => 0));
        const monthInflationBase = monthsOf.map((calendar) => calendar === undefined ? undefined : calendar.map(() => 0));
        line.cells.forEach((cell, i) => {
            const period = pnl.periods[i];
            if (period === undefined)
                return;
            const offset = period.fiscalYear - currentFiscalYear;
            const year = years[offset];
            if (year === undefined)
                return;
            const parts = cellParts(cell);
            year.actual += parts.actual;
            year.scheduled += parts.scheduled;
            year.accrued += parts.accrued;
            const fixed = cell.kind === 'scheduled' ? (line.inflationExemptScheduled?.[i] ?? 0) : 0;
            const alreadyInflated = cell.kind === 'scheduled' ? (line.alreadyInflatedScheduled?.[i] ?? 0) : 0;
            const inflating = parts.scheduled - fixed - alreadyInflated;
            fixedScheduled[offset] = (fixedScheduled[offset] ?? 0) + fixed;
            inflationBase[offset] = (inflationBase[offset] ?? 0) + inflating;
            const index = monthsOf[offset]?.findIndex((calendar) => calendar.year === period.year && calendar.month === period.month) ?? -1;
            const month = months[offset]?.[index];
            if (month !== undefined) {
                month.actual += parts.actual;
                month.accrued += parts.accrued;
                month.scheduled += parts.scheduled;
                const fixedMonth = fixedScheduledMonths[offset];
                if (fixedMonth)
                    fixedMonth[index] = (fixedMonth[index] ?? 0) + fixed;
                const monthBase = monthInflationBase[offset]?.[index];
                if (monthBase !== undefined) {
                    monthInflationBase[offset][index] = monthBase + inflating;
                }
            }
        });
        if (line.key.type === 'expense') {
            years.forEach((year, offset) => {
                const growth = (1 + options.expenseInflationRate) ** offset - 1;
                year.inflation = (inflationBase[offset] ?? 0) * growth;
                months[offset]?.forEach((month, monthIndex) => {
                    month.inflation = (monthInflationBase[offset]?.[monthIndex] ?? 0) * growth;
                });
            });
        }
        const row = {
            id: lineKeyId(line.key),
            label: identity.label,
            direction: line.key.type,
            status: identity.status,
            years,
            months,
            ...(line.inflationExemptScheduled === undefined
                ? {}
                : { fixedScheduled, fixedScheduledMonths }),
        };
        const group = groups.get(identity.groupId) ?? {
            id: identity.groupId,
            label: identity.groupLabel,
            direction: line.key.type,
            lines: [],
            years: [],
            months: [],
        };
        group.lines.push(row);
        groups.set(identity.groupId, group);
    }
    const ordered = [...groups.values()].map((group) => ({
        ...group,
        lines: [...group.lines].sort((a, b) => a.label.localeCompare(b.label)),
        years: sumColumns(group.lines, fiscalYears.length),
        months: sumMonths(group.lines, fiscalYears.length),
    }));
    const byDirection = (direction) => ordered
        .filter((group) => group.direction === direction)
        .sort((a, b) => groupRank(a) - groupRank(b) || a.label.localeCompare(b.label));
    const income = byDirection('income');
    const expense = byDirection('expense');
    return {
        currentFiscalYear,
        fiscalYears,
        monthsOf,
        asOfMonth,
        income,
        expense,
        totals: {
            income: sumColumns(operatingIncome(income), fiscalYears.length),
            expense: sumColumns(expense, fiscalYears.length),
        },
        monthTotals: {
            income: sumMonths(operatingIncome(income), fiscalYears.length),
            expense: sumMonths(expense, fiscalYears.length),
        },
        issues: pnl.issues,
    };
}
function groupRank(group) {
    return group.lines.some((line) => line.status === 'mapped') ? 0 : 1;
}
export function netByYear(model) {
    return model.fiscalYears.map((_, i) => yearTotal(model.totals.income[i] ?? ZERO) - yearTotal(model.totals.expense[i] ?? ZERO));
}
export function firstShortfallYear(model) {
    const net = netByYear(model);
    const index = net.findIndex((value) => value < 0);
    return index === -1 ? undefined : model.fiscalYears[index];
}
export function confirmedShare(amount) {
    const total = yearTotal(amount);
    return total === 0 ? 1 : (amount.actual + amount.scheduled) / total;
}
export function horizonLabel(offset) {
    return offset === 0 ? 'T' : `T+${offset}`;
}
//# sourceMappingURL=viewModel.js.map